import {
  type WorkstreamAuditCompanyGroup,
  WorkstreamAuditReadinessScore,
  type WorkstreamAuditResponse,
  type WorkstreamAuditRow,
} from 'client';

const WORKSTREAM_AUDIT_MINIMUM_LEVEL = 7;
const CHIEF_OF_STAFF_TITLE_PATTERN = /\bchief\s+of\s+staff\b/i;
export const WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER = 'all';
export const WORKSTREAM_AUDIT_COMPANY_READINESS_COMPLETE_FILTER = 'complete';
export const WORKSTREAM_AUDIT_COMPANY_READINESS_ATTENTION_FILTER = 'attention';
export const WORKSTREAM_AUDIT_COMPANY_READINESS_CRITICAL_FILTER = 'critical';

export type WorkstreamAuditCompanyReadinessFilter =
  | typeof WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER
  | typeof WORKSTREAM_AUDIT_COMPANY_READINESS_COMPLETE_FILTER
  | typeof WORKSTREAM_AUDIT_COMPANY_READINESS_ATTENTION_FILTER
  | typeof WORKSTREAM_AUDIT_COMPANY_READINESS_CRITICAL_FILTER;

export type WorkstreamAuditCompanyReadinessCategory = Exclude<
  WorkstreamAuditCompanyReadinessFilter,
  typeof WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER
>;

export type WorkstreamAuditCompanyReadinessCounts = Record<
  WorkstreamAuditCompanyReadinessCategory,
  number
>;

export const isWorkstreamAuditNavEligible = ({
  employeeLevel,
  employeeRole,
  hasCurrentWorkstreamAuditRole,
  isUserAdmin,
}: {
  employeeLevel?: string | null;
  employeeRole?: string | null;
  hasCurrentWorkstreamAuditRole: boolean;
  isUserAdmin?: boolean;
}) => {
  if (
    hasCurrentWorkstreamAuditRole ||
    isUserAdmin ||
    CHIEF_OF_STAFF_TITLE_PATTERN.test(employeeRole?.trim() ?? '')
  ) {
    return true;
  }

  const levelMatch = employeeLevel?.trim().match(/^(?:IC|M)(\d+)$/i);
  return levelMatch ? Number(levelMatch[1]) >= WORKSTREAM_AUDIT_MINIMUM_LEVEL : false;
};

export const countWorkstreamAuditRowsNeedingAttention = (rows?: Array<WorkstreamAuditRow | null>) =>
  (rows ?? []).filter((row) => {
    const score = row?.readiness?.score;
    return (
      score === WorkstreamAuditReadinessScore.POOR ||
      score === WorkstreamAuditReadinessScore.ADEQUATE
    );
  }).length;

export const getCombinedWorkstreamAuditAttentionCount = (audit?: WorkstreamAuditResponse) =>
  audit ? audit.attention_count + countWorkstreamAuditRowsNeedingAttention(audit.dri.rows) : 0;

const getSafeWorkstreamAuditCompanyGroups = (
  groups?: Array<WorkstreamAuditCompanyGroup | null | undefined>
): WorkstreamAuditCompanyGroup[] =>
  (groups ?? []).filter((group): group is WorkstreamAuditCompanyGroup => Boolean(group?.parent));

export const getWorkstreamAuditFailedCheckCount = (
  row?: Pick<WorkstreamAuditRow, 'readiness'> | null
) => {
  const failedCheckIds = row?.readiness?.failed_check_ids;
  return Array.isArray(failedCheckIds) ? failedCheckIds.length : null;
};

export const getWorkstreamAuditCompanyReadinessCategory = (
  row?: Pick<WorkstreamAuditRow, 'readiness'> | null
): WorkstreamAuditCompanyReadinessCategory | null => {
  const failedCheckCount = getWorkstreamAuditFailedCheckCount(row);

  if (failedCheckCount === null) {
    return null;
  }

  if (failedCheckCount >= 3) {
    return WORKSTREAM_AUDIT_COMPANY_READINESS_CRITICAL_FILTER;
  }

  if (failedCheckCount >= 1) {
    return WORKSTREAM_AUDIT_COMPANY_READINESS_ATTENTION_FILTER;
  }

  if (row?.readiness?.score == null) {
    return null;
  }

  return WORKSTREAM_AUDIT_COMPANY_READINESS_COMPLETE_FILTER;
};

export const getWorkstreamAuditCompanyReadinessRows = (
  groups?: Array<WorkstreamAuditCompanyGroup | null | undefined>
): WorkstreamAuditRow[] =>
  getSafeWorkstreamAuditCompanyGroups(groups).flatMap((group) => [
    group.parent,
    ...(group.subworkstreams ?? []).filter((row): row is WorkstreamAuditRow => Boolean(row)),
  ]);

export const workstreamAuditIncludesWorkstream = (
  audit: WorkstreamAuditResponse | undefined,
  workstreamId: number
) => {
  if (!audit) {
    return false;
  }

  return [
    ...(audit.exec_sponsor.rows ?? []),
    ...(audit.dri.rows ?? []),
    ...(audit.reporter.rows ?? []),
    ...(audit.steam_owner.rows ?? []),
    ...getWorkstreamAuditCompanyReadinessRows(audit.company.groups),
  ].some((row) => row.id === workstreamId);
};
